"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Button } from "./button";
import { mergeClassNames } from "./class-names";

const FOCUSABLE_SELECTOR = [
  "button:not([disabled])",
  "[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");
const DIALOG_PORTAL_SELECTOR = "[data-hisani-dialog-portal]";

interface BodyScrollLockState {
  activeLocks: number;
  fixedLocks: number;
  fixedApplied: boolean;
  readonly scrollX: number;
  readonly scrollY: number;
  readonly overflow: string;
  readonly position: string;
  readonly left: string;
  readonly top: string;
  readonly width: string;
}

let bodyScrollLockState: BodyScrollLockState | null = null;
const openDialogs: symbol[] = [];

function registerDialog(dialogId: symbol): () => void {
  openDialogs.push(dialogId);
  return () => {
    const index = openDialogs.lastIndexOf(dialogId);
    if (index !== -1) openDialogs.splice(index, 1);
  };
}

function isTopmostDialog(dialogId: symbol): boolean {
  return openDialogs.at(-1) === dialogId;
}

function resolveDialogPortalContainer(
  portalContainer: Element | DocumentFragment | null | undefined,
): Element | DocumentFragment {
  return (
    portalContainer ??
    document.querySelector(DIALOG_PORTAL_SELECTOR) ??
    document.body
  );
}

function acquireBodyScrollLock(fixBody: boolean): () => void {
  const state =
    bodyScrollLockState ??
    ({
      activeLocks: 0,
      fixedLocks: 0,
      fixedApplied: false,
      scrollX: window.scrollX,
      scrollY: window.scrollY,
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      left: document.body.style.left,
      top: document.body.style.top,
      width: document.body.style.width,
    } satisfies BodyScrollLockState);

  bodyScrollLockState = state;
  state.activeLocks += 1;
  if (fixBody) state.fixedLocks += 1;
  document.body.style.overflow = "hidden";
  synchronizeFixedBody(state);

  let released = false;
  return () => {
    if (released) return;
    released = true;
    state.activeLocks -= 1;
    if (fixBody) state.fixedLocks -= 1;
    synchronizeFixedBody(state);

    if (state.activeLocks !== 0) return;
    document.body.style.overflow = state.overflow;
    bodyScrollLockState = null;
  };
}

function synchronizeFixedBody(state: BodyScrollLockState): void {
  if (state.fixedLocks > 0 && !state.fixedApplied) {
    document.body.style.position = "fixed";
    document.body.style.left = `${-state.scrollX}px`;
    document.body.style.top = `${-state.scrollY}px`;
    document.body.style.width = "100%";
    state.fixedApplied = true;
    return;
  }

  if (state.fixedLocks !== 0 || !state.fixedApplied) return;
  document.body.style.position = state.position;
  document.body.style.left = state.left;
  document.body.style.top = state.top;
  document.body.style.width = state.width;
  state.fixedApplied = false;
  if (state.scrollX !== 0 || state.scrollY !== 0) {
    window.scrollTo(state.scrollX, state.scrollY);
  }
}

export interface DialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly description?: string;
  readonly closeLabel: string;
  readonly portalContainer?: Element | DocumentFragment | null | undefined;
  readonly children?: React.ReactNode;
  readonly footer?: React.ReactNode;
  readonly role?: "dialog" | "alertdialog";
  readonly closeDisabled?: boolean;
  readonly mobileFullscreen?: boolean;
  readonly onClose: () => void;
}

export function Dialog({
  open,
  title,
  description,
  closeLabel,
  portalContainer,
  children,
  footer,
  role = "dialog",
  closeDisabled = false,
  mobileFullscreen = false,
  onClose,
}: DialogProps) {
  const titleId = React.useId();
  const descriptionId = React.useId();
  const panelRef = React.useRef<HTMLDivElement>(null);
  const dialogIdRef = React.useRef(Symbol("dialog"));
  const onCloseRef = React.useRef(onClose);
  const closeDisabledRef = React.useRef(closeDisabled);

  React.useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  React.useEffect(() => {
    closeDisabledRef.current = closeDisabled;
  }, [closeDisabled]);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    const previousFocus =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const hasContainedPortal =
      resolveDialogPortalContainer(portalContainer) !== document.body;
    const releaseBodyScrollLock = acquireBodyScrollLock(!hasContainedPortal);
    const unregisterDialog = registerDialog(dialogIdRef.current);

    const panel = panelRef.current;
    const initialFocus =
      panel?.querySelector<HTMLElement>("[data-dialog-autofocus]") ??
      panel?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    initialFocus?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isTopmostDialog(dialogIdRef.current)) return;
      if (event.key === "Escape") {
        event.preventDefault();
        if (!closeDisabledRef.current) onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || panel === null) {
        return;
      }

      const focusable = Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      );
      if (focusable.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      unregisterDialog();
      releaseBodyScrollLock();
      previousFocus?.focus();
    };
  }, [open, portalContainer]);

  if (!open) {
    return null;
  }

  const portalTarget = resolveDialogPortalContainer(portalContainer);
  const hasContainedPortal = portalTarget !== document.body;
  const dialog = createPortal(
    <div
      className={mergeClassNames(
        "inset-0 z-[var(--z-index-modal)] grid place-items-center overscroll-contain bg-[var(--color-overlay-scrim)] backdrop-blur-sm",
        hasContainedPortal ? "absolute md:fixed" : "fixed",
        mobileFullscreen
          ? "overflow-hidden p-0 sm:overflow-y-auto sm:p-4"
          : "overflow-y-auto p-4",
      )}
      data-hisani-dialog-overlay
    >
      <div
        ref={panelRef}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description === undefined ? undefined : descriptionId}
        tabIndex={-1}
        className={mergeClassNames(
          "@container/dialog flex w-full flex-col overflow-hidden border-border-subtle bg-surface outline-none",
          mobileFullscreen
            ? "h-dvh max-h-dvh max-w-none border-0 sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:max-w-lg sm:rounded-xl sm:border sm:shadow-[var(--elevation-floating)]"
            : "max-h-[calc(100dvh-2rem)] max-w-lg rounded-xl border shadow-[var(--elevation-floating)]",
        )}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-border-subtle px-5 py-4">
          <div className="min-w-0">
            <h2 id={titleId} className="text-heading-medium text-primary">
              {title}
            </h2>
            {description === undefined ? null : (
              <p
                id={descriptionId}
                className="text-body-large mt-2 text-secondary"
              >
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={closeDisabled}
            className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-md text-secondary transition-colors hover:bg-subtle hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-50"
            aria-label={closeLabel}
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </div>
        {children === undefined ? null : (
          <div
            data-slot="dialog-body"
            className="min-h-0 flex-1 overflow-y-auto px-5 py-5"
          >
            {children}
          </div>
        )}
        {footer === undefined ? null : (
          <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-border-subtle px-5 py-4 @min-[30rem]/dialog:flex-row @min-[30rem]/dialog:justify-end">
            {footer}
          </div>
        )}
      </div>
    </div>,
    portalTarget,
  );

  if (!hasContainedPortal) {
    return dialog;
  }

  return (
    <>
      {createPortal(
        <div
          className="fixed inset-0 z-[calc(var(--z-index-modal)-1)]"
          data-hisani-dialog-shield
          aria-hidden="true"
          onTouchMove={(event) => event.preventDefault()}
          onWheel={(event) => event.preventDefault()}
        />,
        document.body,
      )}
      {dialog}
    </>
  );
}

export interface ConfirmationDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly description: string;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  readonly closeLabel: string;
  readonly portalContainer?: Element | DocumentFragment | null | undefined;
  readonly pending?: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

export function ConfirmationDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  closeLabel,
  portalContainer,
  pending = false,
  onConfirm,
  onCancel,
}: ConfirmationDialogProps) {
  return (
    <Dialog
      open={open}
      title={title}
      description={description}
      closeLabel={closeLabel}
      portalContainer={portalContainer}
      role="alertdialog"
      closeDisabled={pending}
      onClose={onCancel}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={pending}
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant="accent"
            onClick={onConfirm}
            disabled={pending}
            data-dialog-autofocus
          >
            {confirmLabel}
          </Button>
        </>
      }
    />
  );
}
